import { useDashboardUIActions, useDashboardUIContext } from "@contexts/dashboardUI";
import { useProjectContext } from "@contexts/projects";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useEffect, useMemo } from "react";
import { Bar, Line } from "react-chartjs-2";
import PropTypes from "prop-types";
import AnnulmentIndicator from "@components/titleAnnulments/AnnulmentIndicator";
import {
  AMPLI_SERIES_LABELS,
  isAmpliCurveVisible,
  isAmpliDisbursementPolicy,
} from "@components/dashboard/financialProjection/cashFlowPlanning";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const colorArr = {
  allReceiv: "#1f1f1f",
  physical: "#ba52f2",
  paid: "#3acff7",
  toPay: "#fc9403",
  disbursement: "#6852F2",
  predicted: "#FFA07A",
  accumulatedPaid: "#FFD700",
  advancementTo: "#3CB371",
  advancementFrom: "#f27752",
  commited: "#2d8a77",
  variation: "#c2bb42",
  balance: '#6eddbc',
  toReceive: "#525252",
  received: "#b6b6b6",
}


const OffCanvasGraph = ({ scheduleOnly }) => {
  const { selectedDetail: { type, item: selectedItem }, graphData, activeProject } = useProjectContext();
  const { barChartDate } = useDashboardUIContext()
  const { setIsLoading } = useDashboardUIActions()
  // Previsto/medido/físico-financeiro decidido pela obra (`physicalFinancial.available`,
  // sobre a série completa), não pelo que este item específico tem no momento --
  // consistente com FinancialBarChart/TableItem/ProjectLineChart
  // (serve-works-without-optional-sources, D4/D5).
  const physicalFinancialAvailable = activeProject?.cashFlowPlanning?.physicalFinancial?.available === true;
  // financial-projection-integration: sob política Ampli o cronograma do ERP
  // (`disbursements`/`takenTo`/`takenFrom`) nunca aparece — são outro planejamento — e a
  // curva Ampli só desenha quando a obra tem curva visível (`ready`/`stale`) E o item
  // pedido tem valor nela; do contrário nenhuma série de desembolso é desenhada, nunca
  // uma série zerada (scenarios "Item sem valor" / "Obra sem curva disponível").
  const isAmpliPolicy = isAmpliDisbursementPolicy(activeProject?.cashFlowPlanning);

  const {
    toPayValues = [],
    paidValues = [],
    disbursements = [],
    physicals = [],
    measured = [],
    takenTo = { byMonth: [] },
    takenFrom = { byMonth: [] },
    scheduleCurves = [],
    ampliDisbursement: ampliDisbursementSeries = [],
    cashProjection: cashProjectionSeries = [],
  } = graphData ?? {}
  const ampliVisible = isAmpliPolicy
    && isAmpliCurveVisible(activeProject)
    && (ampliDisbursementSeries.length > 0 || cashProjectionSeries.length > 0);

  useEffect(() => {
    setIsLoading(true)
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 500)
    return () => clearTimeout(timer)
  }, [barChartDate])

  const [paidList, disbursementList, toPayList, takenFromList, takenToList, dates, physicalsList, measuredList, ampliDisbursementList, cashProjectionList] = useMemo(() => {

    const paidDates = paidValues.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator;
    }, [])

    const disbursementDates = disbursements.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator
    }, [])

    const ampliDisbursementDates = ampliDisbursementSeries.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator
    }, [])

    const cashProjectionDates = cashProjectionSeries.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator
    }, [])

    const toPayDates = toPayValues.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator
    }, [])

    const physicalsDates = physicals.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator
    }, [])

    const measuredDates = measured.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator
    }, [])


    const takenFromDates = takenFrom?.byMonth?.reduce((accumulator, item) => {
      accumulator.push(item.from)
      return accumulator
    }, [])


    const takenToDates = takenTo?.byMonth?.reduce((accumulator, item) => {
      accumulator.push(item.to)
      return accumulator
    }, [])

    const dates = [...paidDates, ...disbursementDates, ...toPayDates, ...takenFromDates || [], ...takenToDates || [], ...physicalsDates, ...measuredDates, ...ampliDisbursementDates, ...cashProjectionDates]
      .filter((item, index, self) => index === self.findIndex((indexedItem) => indexedItem === item))
      .sort((current, next) => {
        const [currentMonth, currentYear] = current.split("/")
        const [nextMonth, nextYear] = next.split("/")
        if (currentYear !== nextYear) {
          return Number(currentYear) - Number(nextYear);
        } else {
          return Number(currentMonth) - Number(nextMonth);
        }
      })

    const {
      paidMap, disbursementMap, toPayMap, takenFromMap, takenToMap, physicalsMap, measuredMap,
      ampliDisbursementMap, cashProjectionMap,
    } = dates.reduce((acc, item) => {
      acc.paidMap.set(item, paidValues.find(paidItem => paidItem.date === item)?.value ?? 0)
      acc.disbursementMap.set(item, disbursements.find(disbursementItem => disbursementItem.date === item)?.value ?? 0)
      acc.toPayMap.set(item, toPayValues.find(toPayItem => toPayItem.date === item)?.value ?? 0)
      acc.takenFromMap.set(item, takenFrom?.byMonth?.find(takenItem => takenItem.from === item)?.value ?? 0)
      acc.takenToMap.set(item, takenTo?.byMonth?.find(takenItem => takenItem.to === item)?.value ?? 0)
      acc.physicalsMap.set(item, physicals.find(physicalsItem => physicalsItem.date === item)?.value)
      acc.measuredMap.set(item, measured.find(m => m.date === item)?.value)
      acc.ampliDisbursementMap.set(item, ampliDisbursementSeries.find(i => i.date === item)?.value ?? 0)
      acc.cashProjectionMap.set(item, cashProjectionSeries.find(i => i.date === item)?.value ?? 0)
      return acc;
    }, {
      paidMap: new Map(), disbursementMap: new Map(), toPayMap: new Map(), takenFromMap: new Map(),
      takenToMap: new Map(), physicalsMap: new Map(), measuredMap: new Map(),
      ampliDisbursementMap: new Map(), cashProjectionMap: new Map(),
    })

    return [
      Array.from(paidMap.values()), Array.from(disbursementMap.values()), Array.from(toPayMap.values()),
      Array.from(takenFromMap.values()), Array.from(takenToMap.values()), dates,
      Array.from(physicalsMap.values()), Array.from(measuredMap.values()),
      Array.from(ampliDisbursementMap.values()), Array.from(cashProjectionMap.values()),
    ]
  }, [paidValues, disbursements, toPayValues, takenFrom, takenTo, physicals, measured, ampliDisbursementSeries, cashProjectionSeries])

  const disbWithAdvanceList = disbursementList.map((i, idx) => i + takenToList[idx] - takenFromList[idx])

  let graphDataSet = [
    ...(physicalFinancialAvailable ? [{
      label: "Previsto",
      borderColor: colorArr.physical,
      backgroundColor: colorArr.physical,
      data: physicalsList,
      stack: 'physicals'
    }] : []),
    // financial-projection-integration: sob política Ampli, o cronograma do ERP nunca
    // aparece (outro planejamento) e a curva Ampli só desenha quando o item pedido tem
    // valor nela — nunca uma série zerada por ausência.
    ...(isAmpliPolicy
      ? (ampliVisible ? [
        {
          label: AMPLI_SERIES_LABELS.ampliDisbursement,
          borderColor: colorArr.disbursement,
          backgroundColor: colorArr.disbursement,
          data: ampliDisbursementList,
          stack: 'disbursement',
        },
        {
          label: AMPLI_SERIES_LABELS.cashProjection,
          borderColor: colorArr.predicted,
          backgroundColor: colorArr.predicted,
          data: cashProjectionList,
          stack: 'disbursementWithAdv',
        },
      ] : [])
      : [
        {
          label: "Desembolso",
          borderColor: colorArr.disbursement,
          backgroundColor: colorArr.disbursement,
          data: disbursementList,
          stack: 'disbursement'
        },
        {
          label: "Desembolso com ajustes",
          borderColor: colorArr.predicted,
          backgroundColor: colorArr.predicted,
          data: disbWithAdvanceList,
          stack: 'disbursementWithAdv',
        },
      ]),
    ...(physicalFinancialAvailable ? [{
      label: "Medido",
      borderColor: "#16A34A",
      backgroundColor: "#16A34A",
      data: measuredList,
      stack: 'measured'
    }] : []),
    {
      label: "Pago",
      borderColor: colorArr.paid,
      backgroundColor: colorArr.paid,
      data: paidList,
      stack: 'paid'
    },
    {
      label: "A pagar",
      borderColor: colorArr.toPay,
      backgroundColor: colorArr.toPay,
      data: toPayList,
      stack: 'paid',
    },
  ]

  // "Movido para"/"Movido de" descrevem ajustes do cronograma do ERP (`advancements`):
  // sob política Ampli são outro mecanismo (`disbursement_adjustments`, já netado na
  // própria curva) e não se somam ao gráfico.
  if (!isAmpliPolicy && activeProject.appropriation_level === type) {
    graphDataSet = [
      ...graphDataSet,
      {
        label: "Movido para",
        borderColor: colorArr.advancementTo,
        backgroundColor: colorArr.advancementTo,
        data: takenToList
      },
      {
        label: "Movido de",
        borderColor: colorArr.advancementFrom,
        backgroundColor: colorArr.advancementFrom,
        data: takenFromList
      },
    ]
  }

  const graphOptions = {
    scales: {
      x: {
        stacked: true,
        display: true,
        title: {
          display: true,
          text: 'Mês/Ano',
          font: {
            weight: 'bold'
          }
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Valor',
          font: {
            weight: 'bold'
          }
        }
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        display: true,
      },
      title: {
        display: false,
        text: "Projeção Financeira",
        align: "start",
        color: "black",
        font: {
          size: "18px"
        }
      },
      datalabels: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const dataset = tooltipItem.dataset;
            return `${dataset.label}: ${tooltipItem.formattedValue}`;
          }
        }
      }
    }
  }

  const scheduleChartData = useMemo(() => ({
    labels: scheduleCurves.map((point) => point.date),
    datasets: [
      {
        label: "Linha de base",
        borderColor: "#245AE4",
        backgroundColor: "#245AE4",
        borderDash: [6, 4],
        borderWidth: 2,
        data: scheduleCurves.map((point) => point.baseline ?? undefined),
      },
      {
        label: "Previsto",
        borderColor: "#FD7E14",
        backgroundColor: "#FD7E14",
        borderWidth: 3,
        data: scheduleCurves.map((point) => point.predicted ?? undefined),
      },
      {
        label: "Medido",
        borderColor: "#16A34A",
        backgroundColor: "#16A34A",
        borderWidth: 3,
        data: scheduleCurves.map((point) => point.measured ?? undefined),
      },
    ],
  }), [scheduleCurves]);

  if (scheduleOnly) {
    if (!physicalFinancialAvailable) return null;
    return <Line
      options={{
        ...graphOptions,
        scales: {
          ...graphOptions.scales,
          x: { ...graphOptions.scales.x, stacked: false },
        },
      }}
      data={scheduleChartData}
    />
  }

  // Pago e a pagar do item: indicador só com título anulado apropriado neste item (EAP).
  return <>
    <AnnulmentIndicator
      summary={activeProject?.titleAnnulments}
      kind="both"
      wbsCode={selectedItem?.wbs_code_id ?? ""}
      placement="row"
    />
    <Bar options={graphOptions}
      data={{
        labels: dates,
        datasets: graphDataSet
      }} />
  </>


}

OffCanvasGraph.propTypes = {
  scheduleOnly: PropTypes.bool,
};

OffCanvasGraph.defaultProps = {
  scheduleOnly: false,
};

export default OffCanvasGraph;
